import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Avatar, Button, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import useAppState from '../../../../hooks/useAppStateSession'
import { loadCloudFunctionsHandler } from '../../../../apiLayer/handlers'
import toast from 'react-hot-toast'
import { ActivityEmailSent } from '../../../../../../pure-js/types/Sprint'

interface Participant {
  userId?: string
  name: string
  email?: string
  teamId?: string
  completedOnboardingSteps?: string[]
}

interface Team {
  name: string
  teamId?: string
  tempTeamId: string
}

interface UpcomingSessionParticipantItemProps {
  participant: Participant
  teams: Team[]
  sprintId: string
  activityId: string
  emailsSent?: ActivityEmailSent[]
}

/**
 * Get display name for participant
 * If name is missing or empty, returns "Gamer [ID]"
 */
const getParticipantDisplayName = (participant: Participant, t: any): string => {
  if (participant.name && participant.name.trim() !== '') {
    return participant.name
  }
  // If no name, show "Gamer" + participant ID with translation
  return t('facilitator.sessions.participantGamerWithId', { id: participant.userId || t('common.unknown') })
}

/**
 * Get initials from name
 */
const getInitials = (name: string): string => {
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

/**
 * Get team name for participant
 */
const getTeamName = (participant: Participant, teams: Team[], t: any): string => {
  if (!participant.teamId) return t('facilitator.sessions.noTeam')

  const team = teams.find((t) => t.teamId === participant.teamId || t.tempTeamId === participant.teamId)
  return team?.name || t('facilitator.sessions.unknownTeam')
}

/**
 * Get onboarding progress for participant
 */
const getOnboardingProgress = (participant: Participant) => {
  const completedSteps = participant.completedOnboardingSteps || []
  const displayedSteps = ['Introduction', 'Assessment', 'GameIntroduction']

  // Count completed steps for display (only the first three)
  const completedDisplayedSteps = completedSteps.filter((step) => displayedSteps.includes(step))

  return {
    completed: completedDisplayedSteps.length,
    total: displayedSteps.length,
    steps: {
      introduction: completedSteps.includes('Introduction'),
      assessment: completedSteps.includes('Assessment'),
      gameIntroduction: completedSteps.includes('GameIntroduction'),
      tutorial: completedSteps.includes('Tutorial')
    }
  }
}

/**
 * Upcoming Session Participant Item
 * Shows avatar, name, email, team, progress, and send reminder button
 * Matches the design from the provided image
 */
export const UpcomingSessionParticipantItem: React.FC<UpcomingSessionParticipantItemProps> = ({
  participant,
  teams,
  sprintId,
  activityId,
  emailsSent = []
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { state } = useAppState()
  const [isSending, setIsSending] = useState(false)
  const progress = getOnboardingProgress(participant)
  const hasReminderLogged = useMemo(
    () => emailsSent.some((email) => email.userId === participant.userId && email.type === 'activity-reminder'),
    [emailsSent, participant.userId]
  )
  const [hasSentReminder, setHasSentReminder] = useState(hasReminderLogged)

  useEffect(() => {
    if (hasReminderLogged) {
      setHasSentReminder(true)
    }
  }, [hasReminderLogged])

  const handleSendReminder = async () => {
    if (!participant.userId) {
      console.error('No userId for participant:', participant)
      toast.error(t('facilitator.sessions.messages.cannotSendReminderNoUserId'))
      return
    }

    setIsSending(true)

    try {
      const cloudFunctionsHandler = await loadCloudFunctionsHandler()
      const result = await cloudFunctionsHandler.sendSprintActivityReminder(
        sprintId,
        participant.userId,
        activityId,
        state as any // SessionState to State conversion
      )

      if (result.success) {
        setHasSentReminder(true)
        toast.success(
          t('facilitator.sessions.messages.reminderSentToUser', { name: participant.name || participant.email })
        )
      } else {
        console.error('❌ Failed to send reminder:', result.error)
        toast.error(result.error || t('facilitator.sessions.messages.failedToSendReminder'))
      }
    } catch (error) {
      console.error('❌ Error sending reminder:', error)
      toast.error(t('facilitator.sessions.messages.errorSendingReminder'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 2 : 0,
        p: 2,
        borderRadius: '10px',
        background:
          'radial-gradient(50.19% 50.19%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), rgba(46, 79, 95, 0.5)',
        backgroundBlendMode: 'soft-light, normal',
        border: '1px solid rgba(255, 255, 255, 0.04)',
        boxShadow: 'inset 0 1px 0 0 rgba(255, 255, 255, 0.08)',
        mb: 1.5
      }}
    >
      {/* Left Side: Avatar and Info */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: 2,
          flex: 1,
          minWidth: 0
        }}
      >
        {/* Avatar + Name/Email row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              backgroundColor: '#5C614A', // Olive-brown from image
              color: INTERAX_COLORS.sun, // Light orange/gold initials
              fontSize: '0.875rem',
              fontWeight: 'bold',
              borderRadius: '6px' // Slightly rounded corners
            }}
          >
            {getInitials(getParticipantDisplayName(participant, t))}
          </Avatar>

          {/* Name and Email - responsive widths */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: INTERAX_COLORS.white,
                fontSize: '0.875rem',
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {getParticipantDisplayName(participant, t)}
            </Typography>
            <Typography
              sx={{
                color: INTERAX_COLORS.thunderGrey,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {participant.email || t('facilitator.sessions.noEmail')}
            </Typography>
          </Box>
        </Box>

        {/* Team and Progress - wraps on mobile */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? 1 : 3,
            flex: isMobile ? '0 0 auto' : 1,
            minWidth: 0
          }}
        >
          {/* Team */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: INTERAX_COLORS.white,
                fontSize: '0.875rem',
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {getTeamName(participant, teams, t)}
            </Typography>
            <Typography
              sx={{
                color: INTERAX_COLORS.thunderGrey,
                fontSize: '0.75rem'
              }}
            >
              {t('facilitator.sessions.team')}
            </Typography>
          </Box>

          {/* Progress */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: INTERAX_COLORS.white,
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}
            >
              {t('facilitator.sessions.stepsCompleted', { completed: progress.completed, total: progress.total })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  color: progress.steps.introduction ? INTERAX_COLORS.green : INTERAX_COLORS.redError,
                  fontSize: '0.75rem'
                }}
              >
                {progress.steps.introduction ? '✓' : '×'}
              </Typography>
              <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '0.75rem' }}>
                {t('facilitator.sessions.onboardingSteps.intro')}
              </Typography>
              <Typography
                sx={{
                  color: progress.steps.assessment ? INTERAX_COLORS.green : INTERAX_COLORS.redError,
                  fontSize: '0.75rem'
                }}
              >
                {progress.steps.assessment ? '✓' : '×'}
              </Typography>
              <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '0.75rem' }}>
                {t('facilitator.sessions.onboardingSteps.assessment')}
              </Typography>
              <Typography
                sx={{
                  color: progress.steps.gameIntroduction ? INTERAX_COLORS.green : INTERAX_COLORS.redError,
                  fontSize: '0.75rem'
                }}
              >
                {progress.steps.gameIntroduction ? '✓' : '×'}
              </Typography>
              <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '0.75rem' }}>
                {t('facilitator.sessions.onboardingSteps.gameIntro')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Side: Send Reminder Button - full width on mobile */}
      <Button
        variant="contained"
        disabled={hasSentReminder || isSending}
        onClick={handleSendReminder}
        startIcon={<EnvelopeIcon className="w-4 h-4" />}
        sx={{
          backgroundColor: hasSentReminder ? INTERAX_COLORS.thunderGrey : INTERAX_COLORS.sun,
          color: hasSentReminder ? INTERAX_COLORS.white : INTERAX_COLORS.offBlack,
          borderRadius: '6px',
          px: 2,
          py: 1,
          fontSize: '0.75rem',
          fontWeight: '500',
          textTransform: 'none',
          width: isMobile ? '100%' : 'auto',
          alignSelf: isMobile ? 'stretch' : 'auto',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: hasSentReminder ? INTERAX_COLORS.thunderGrey : '#FFBC66'
          },
          '&:disabled': {
            backgroundColor: INTERAX_COLORS.thunderGrey,
            color: INTERAX_COLORS.white
          }
        }}
      >
        {isSending
          ? t('facilitator.sessions.actions.sending')
          : hasSentReminder
            ? t('facilitator.sessions.actions.reminderSent')
            : t('facilitator.sessions.actions.sendReminder')}
      </Button>
    </Box>
  )
}
